import type {
    AssetMemoryAnalysisOptions,
    AssetMemoryDiagnostic,
    ParsedTarget,
    ParsedTargetMemoryRegion,
    ParsedTargetMemoryResult
} from './AssetMemoryTypes';
import { parseDisplay } from './common';
import { hasXmlParseErrorElement } from '../../../services/xmlParseError';
import { parseXmlWithDomParser } from '../../../services/xmlParser';

const SIZE_UNITS: Record<string, number> = {
    b: 1,
    byte: 1,
    bytes: 1,
    k: 1024,
    kb: 1024,
    kib: 1024,
    m: 1024 * 1024,
    mb: 1024 * 1024,
    mib: 1024 * 1024,
    g: 1024 * 1024 * 1024,
    gb: 1024 * 1024 * 1024,
    gib: 1024 * 1024 * 1024
};

const COLOR_FORMAT_BITS_PER_PIXEL: Record<string, number> = {
    l8: 8,
    i1: 1,
    i2: 2,
    i4: 4,
    i8: 8,
    a1: 1,
    a2: 2,
    a4: 4,
    a8: 8,
    argb8888: 32,
    xrgb8888: 32,
    rgb565: 16,
    rgb565a8: 24,
    argb8565: 24,
    rgb888: 24
};

/**
 * Parses human-readable byte sizes such as "512K" or "1MB".
 * Returns null when the value is empty, malformed, or uses an unsupported unit.
 */
export const parseDataSizeToBytes = (value: string | null | undefined): number | null => {
    const normalized: string = (value ?? '').trim().replace(/\s+/g, '').toLowerCase();
    if (!normalized) return null;

    const match: RegExpMatchArray | null = normalized.match(/^(\d+(?:\.\d+)?)([a-z]*)$/);
    if (!match) return null;

    const amount: number = Number(match[1]);
    const unit: string = match[2] || 'b';
    const multiplier: number | undefined = SIZE_UNITS[unit];

    if (!Number.isFinite(amount) || multiplier === undefined) return null;
    return Math.round(amount * multiplier);
};

/**
 * Parses memory bandwidth values such as "1MB/s" into bytes per second.
 * Bandwidth intentionally requires the "/s" suffix so plain sizes are not accepted by mistake.
 */
export const parseBandwidthToBytesPerSecond = (value: string | null | undefined): number | null => {
    const normalized: string = (value ?? '').trim().replace(/\s+/g, '').toLowerCase();
    if (!normalized.endsWith('/s')) return null;
    return parseDataSizeToBytes(normalized.slice(0, -2));
};

/**
 * Reads the PNG IHDR width and height directly from a binary PNG header.
 * This avoids pulling image decoding into the shared analyzer just to estimate C-array size.
 */
export const readPngDimensions = (data: Uint8Array): { width: number; height: number } | null => {
    if (data.length < 24) return null;
    const signature: readonly number[] = [137, 80, 78, 71, 13, 10, 26, 10];
    for (let i: number = 0; i < signature.length; i++) if (data[i] !== signature[i]) return null;

    const view: DataView = new DataView(data.buffer, data.byteOffset, data.byteLength);
    return {
        width: view.getUint32(16),
        height: view.getUint32(20)
    };
};

/**
 * Estimates the byte size of generated image data from pixel count and LVGL color format.
 * Returns null for unsupported formats such as raw/auto where the byte size depends on conversion output.
 */
export const estimateImageDataSizeBytes = (
    width: number,
    height: number,
    colorFormat: string | null | undefined
): number | null => {
    const bitsPerPixel: number | undefined = COLOR_FORMAT_BITS_PER_PIXEL[(colorFormat ?? '').trim().toLowerCase()];
    if (!bitsPerPixel || width <= 0 || height <= 0) return null;

    return Math.ceil((width * height * bitsPerPixel) / 8);
};

/**
 * Normalizes XML input into a Document and appends diagnostics instead of throwing.
 * Tests and non-browser callers can inject a parser when DOMParser is not globally available.
 */
export const getXmlDocument = (
    xml: string | Document,
    parseXml: AssetMemoryAnalysisOptions['parseXml'],
    diagnostics: AssetMemoryDiagnostic[],
    fileName: string
): Document | null => {
    if (typeof xml !== 'string') return xml;

    const parser: ((xml: string) => Document) | undefined =
        parseXml ??
        (typeof DOMParser !== 'undefined'
            ? (value: string): Document => parseXmlWithDomParser(value, 'application/xml')
            : undefined);

    if (!parser) {
        diagnostics.push({
            severity: 'error',
            code: 'missing-xml-parser',
            message: `Cannot parse ${fileName} because no XML parser is available.`
        });
        return null;
    }

    const document: Document = parser(xml);
    if (hasXmlParseErrorElement(document)) {
        diagnostics.push({
            severity: 'error',
            code: 'invalid-xml',
            message: `${fileName} is not valid XML.`
        });
        return null;
    }

    return document;
};

/**
 * Parses target memory declarations from project.xml and reports structural/value diagnostics.
 * Invalid memory rows are still returned so callers can show partial reports beside diagnostics.
 */
export const parseTargetMemoryRegions = (
    projectXml: string | Document,
    options: AssetMemoryAnalysisOptions = {}
): ParsedTargetMemoryResult => {
    const diagnostics: AssetMemoryDiagnostic[] = [];
    const projectDocument: Document | null = getXmlDocument(projectXml, options.parseXml, diagnostics, 'project.xml');

    if (!projectDocument) return { targets: [] as ParsedTarget[], diagnostics };

    const targetElements: Element[] = Array.from(projectDocument.getElementsByTagName('target')).filter(
        (target: Element) => target.parentElement?.tagName.toLowerCase() === 'targets'
    );

    const seenTargets: Set<string> = new Set();
    const targets: ParsedTarget[] = targetElements.map((target: Element, targetIndex: number) => {
        const targetName: string = target.getAttribute('name')?.trim() || `target${targetIndex + 1}`;

        if (!target.getAttribute('name')) {
            diagnostics.push({
                severity: 'error',
                code: 'missing-target-name',
                message: `Target at index ${targetIndex + 1} is missing a name.`
            });
        }

        if (seenTargets.has(targetName)) {
            diagnostics.push({
                severity: 'error',
                code: 'duplicate-target-name',
                targetName,
                message: `Target "${targetName}" is declared more than once.`
            });
        }
        seenTargets.add(targetName);

        const seenMemories: Set<string> = new Set();
        const memories: ParsedTargetMemoryRegion[] = Array.from(target.children)
            .filter((child: Element) => child.tagName.toLowerCase() === 'memory')
            .map((memory: Element, memoryIndex: number): ParsedTargetMemoryRegion => {
                const memoryName: string = memory.getAttribute('name')?.trim() || `memory${memoryIndex + 1}`;
                const size: string = memory.getAttribute('size')?.trim() || '';
                const sizeBytes: number | null = parseDataSizeToBytes(size);
                const bandwidth: string | undefined = memory.getAttribute('bandwidth')?.trim() || undefined;
                const bandwidthBytesPerSecond: number | null = bandwidth
                    ? parseBandwidthToBytesPerSecond(bandwidth)
                    : null;

                if (!memory.getAttribute('name')) {
                    diagnostics.push({
                        severity: 'error',
                        code: 'missing-memory-name',
                        targetName,
                        message: `Memory region at index ${memoryIndex + 1} in target "${targetName}" is missing a name.`
                    });
                }

                if (seenMemories.has(memoryName)) {
                    diagnostics.push({
                        severity: 'error',
                        code: 'duplicate-memory-name',
                        targetName,
                        memoryName,
                        message: `Memory region "${memoryName}" is declared more than once in target "${targetName}".`
                    });
                }
                seenMemories.add(memoryName);

                if (sizeBytes === null) {
                    diagnostics.push({
                        severity: 'error',
                        code: 'invalid-memory-size',
                        targetName,
                        memoryName,
                        message: `Memory region "${memoryName}" in target "${targetName}" has invalid size "${size}".`
                    });
                }

                if (bandwidth && bandwidthBytesPerSecond === null) {
                    diagnostics.push({
                        severity: 'error',
                        code: 'invalid-memory-bandwidth',
                        targetName,
                        memoryName,
                        message: `Memory region "${memoryName}" in target "${targetName}" has invalid bandwidth "${bandwidth}".`
                    });
                }

                return {
                    name: memoryName,
                    size,
                    sizeBytes,
                    section: memory.getAttribute('section')?.trim() || undefined,
                    bandwidth,
                    bandwidthBytesPerSecond
                };
            });

        return {
            name: targetName,
            memories,
            display: parseDisplay(
                target,
                // Get the one display under the target
                Array.from(target.children).find((child) => child.tagName === 'display'),
                targetIndex
            )
        };
    });

    return { targets, diagnostics };
};
